import { mat4, vec3 } from 'gl-matrix';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { RenderedTexture } from '../texture';
import { ViewFrustum } from '../util';
import { DepthMapRenderer, DepthMode } from '../renderer';
import { InstanceManager } from '../renderer';
import { RenderTexture, createColorTexture, createFramebuffer } from '../renderer/RenderTarget';
import { FullscreenQuad } from '../renderer/FullscreenQuad';
import { Scene } from '../scene/Scene';

const CASCADE_COUNT = 4;

export class CascadedShadowMap {
  cascadeTextures: RenderedTexture[] = [];
  cascadeViewMatrices: mat4[] = [];
  cascadeSplits: number[] = [];
  /**
   * World-space size of one shadow-map texel per cascade — used internally
   * for the texel-grid snap that prevents shadow edge swimming as the camera
   * walks. No longer exposed to shaders (EVSM sampling doesn't need it).
   */
  private cascadeTexelSizes: number[] = [];

  /**
   * EVSM moments per cascade — RG32F (M1=exp(c·d), M2=exp(2c·d)) populated
   * by the `csm-moments-resolve` + `csm-moments-blur` + `csm-moments-blend`
   * shaders right after each cascade's depth pass. Sampled by
   * `csm_shadow.glsl` instead of the raw depth texture so soft shadows come
   * from a hardware-bilinear lookup on a smooth Chebyshev bound (no
   * binary-compare texel-quantization stripes on slow-varying terrain at
   * low sun). For throttled cascades (1/2/3) the texture also accumulates
   * a temporal EMA over fresh blurred moments, smoothing the discrete
   * per-update jumps that would otherwise read as "shadow blotches" as the
   * sun rotates.
   */
  momentsTextures: RenderTexture[] = [];
  private momentsFBOs: WebGLFramebuffer[] = [];
  /**
   * Two scratch buffers for the resolve→blurH→blurV→EMA-blend chain.
   * `momentsTextures[i]` itself holds the temporally-smoothed moments that
   * the lighting pass samples and that serve as `prev` for the next blend —
   * we cannot use it as a working buffer or we'd destroy history.
   */
  private momentsScratch1!: RenderTexture;
  private momentsScratch1FBO!: WebGLFramebuffer;
  private momentsScratch2!: RenderTexture;
  private momentsScratch2FBO!: WebGLFramebuffer;
  /**
   * Per-cascade light VP that produced the moments currently sitting in
   * `momentsTextures[i]`. Used by the blend pass to reproject `prev`
   * samples into the world position represented by each *current* texel,
   * so the temporal EMA stays consistent across matrix changes (sun
   * rotation, camera-snap shift, cache rebuild). `null` ⇒ no valid
   * history yet, blend pass installs fresh verbatim.
   */
  private prevMomentsLightVP: (mat4 | null)[] = [];
  private fullscreenQuad!: FullscreenQuad;

  private depthRenderer!: DepthMapRenderer;
  private lightDirection!: vec3;
  private shadowMapSize: number;
  private frameCounter = 0;
  /**
   * True until every cascade has rendered at least once. The first call
   * forces all cascades to render even if their normal staggered schedule
   * would skip them. Without this, cascades 2/3 stay zero-initialized for
   * 2–3 frames after init, and the long-shadow overlay (which samples
   * cascade 3 unconditionally) reads `M1=M2=0` → variance pinned to
   * MIN_VARIANCE, Chebyshev returns ≈0 → entire scene reads fully shadowed
   * during the first ~50ms of every scene load.
   */
  private firstRender = true;

  /** LOD distance multiplier — set by the renderer before each frame. */
  public lodDistanceScale = 1.0;

  /**
   * Per-cascade render interval: cascade i re-renders every cascadeUpdateRates[i] frames.
   * Cascade 0 (nearest) renders every frame; cascades 1+ are throttled because their
   * static caches mean the per-frame work is just blit + dynamic-object overlay. The rate
   * controls how often that overlay refreshes for moving objects in the far cascades.
   * Cascade 3 (mountain-scale) only carries static casters so its rate is purely the
   * cache-rebuild trigger frequency.
   * Set all to 1 to disable temporal CSM.
   */
  public cascadeUpdateRates = [1, 3, 6, 12];

  private staticCascadeTextures: RenderedTexture[] = [];
  private lastStaticCenter: (vec3 | null)[] = [];
  /**
   * Cascades whose static cache was rebuilt during the most recent render() call,
   * as a bitmask (bit i ↔ cascade i). Surfaced through RenderStats so the perf
   * overlay can show how often the cache rebuilds — a sustained nonzero mask
   * during fast camera motion means every frame is paying to re-rasterise all
   * static shadow casters into the cache, which is expensive on scatter-heavy
   * scenes. Reset at the top of render().
   */
  public lastStaticCacheRebuildMask = 0;
  /**
   * Light direction the static cache was last rendered with. The cache stores
   * static-caster depth from the light's POV — when the sun rotates, that depth
   * is wrong even if the camera (and cascade center) hasn't moved. Invalidate
   * when the angular delta exceeds `staticCacheLightAngleThreshold`.
   */
  private lastStaticLightDirection: (vec3 | null)[] = [];
  /**
   * Sun-rotation staleness threshold in radians. ~0.11° — at default 10°/s
   * cycle speed (0.5° per cascade-1 update), cache invalidates every render;
   * at sub-degree-per-second cycles the cache survives multiple updates.
   */
  public staticCacheLightAngleThreshold = 0.002;
  /**
   * Per-cascade locked light-space matrices. Recomputed only when the static
   * cache rebuilds. Locking matrix and cache together prevents the inter-frame
   * drift that's visible as shadow shimmer when the camera walks slowly: if the
   * matrix updated every frame while the cache lagged behind, sampling would
   * land on cached depths from a different cascade alignment.
   */
  private cachedLightView: (mat4 | null)[] = [];
  private cachedLightProjection: (mat4 | null)[] = [];

  /**
   * Per-cascade staleness threshold in texels. When the cascade center has drifted more
   * than this many texels since the static geometry was last rendered, the static cache
   * is rebuilt. Set to Infinity to disable caching for that cascade.
   * Cascade 0 updates too frequently and at too small a texel size to benefit; cascades
   * 1+ are cached, with progressively looser thresholds since their world-space texel
   * footprint grows roughly geometrically.
   */
  public staticCacheThresholdTexels: number[] = [Infinity, 4, 8, 16];

  /**
   * Per-cascade max distance for *animated* shadow casters. Animated casters
   * whose bounding-sphere center is farther than this from the camera are
   * skipped from that cascade's depth pass — full skinning of distant animated
   * models is expensive and their on-screen shadow is sub-pixel anyway.
   * Static non-instanced casters (terrain) are NOT filtered by this cap; see
   * `DepthMapRenderer.maxDynamicShadowDistance`.
   * Cascades 0/1 cover the close foreground so they don't need filtering;
   * cascade 2 reaches ~235m but past 50m animated shadows aren't worth the
   * cost; cascade 3 (mountain-scale) is left unfiltered for completeness.
   */
  public maxDynamicShadowDistance: number[] = [Infinity, Infinity, 50, Infinity];

  /**
   * Per-cascade temporal-EMA alpha for the moments blend
   * (`out = (1-α)·prev + α·fresh`). Lower = smoother (more lag, fewer jumps),
   * higher = snappier (faster convergence, visible jumps).
   *
   * Cascade 0 uses 1.0 (no EMA): it renders every frame, so there are no
   * throttling-induced jumps to smooth — EMA would just blur the sharp
   * player/foreground shadow as the camera moves. Cached cascades 1–3 use
   * 0.3, which converges over ~6 update-frames; combined with their own
   * `cascadeUpdateRates` of 3/6/12, that gives an effective half-life of
   * 0.1–0.4s real time at 60fps — fast enough to track day/night sun
   * motion without the "blotches converging" pop.
   */
  public momentsBlendAlpha: number[] = [1.0, 0.3, 0.3, 0.3];

  /**
   * Per-cascade distance the ortho extends *toward the sun* from the cascade
   * center to capture casters that aren't inside the cascade slice but whose
   * shadows project into it.
   *
   * Per-cascade because a single global value (e.g. 2500m for mountain shadows
   * in cascade 3) inflates cascade 0's Z-range from ~80m to ~2550m. Window-space
   * `gl.polygonOffset(3, 6)` translates to world-space proportional to the
   * cascade's Z-range, so a deep ortho on the near cascade pulls the recorded
   * caster depth ~30× farther back in world units, detaching close shadows
   * (player shadow Peter Pans). Cascade 3 keeps the deep 2500m extent for
   * mountain-scale occluders; closer cascades shrink so polygon-offset's
   * world-space pull stays tight.
   *
   * Per-cascade values are tuned for max occluder height that can cast onto
   * that cascade's receiver disk at low sun (~15° elevation). Taller/farther
   * occluders project onto receivers in a higher cascade and are redrawn there.
   * Cascade 0 (60m): catches ~15m-tall occluders within 60m — towers, cliffs,
   * large trees. Cascade 3 (2500m): full mountain coverage on a ~2.6km world.
   */
  public casterDistanceToSun: number[] = [60, 200, 800, 2500];

  /**
   * Upper bound on cascade 0's far split, in world units. Trades close-shadow
   * texel precision for occluder coverage: 25m means cascade 0's ortho is
   * ~±25m horizontally, which lets it capture mountain casters tens of meters
   * away from the camera. Smaller values starve cascade 0 of distant casters
   * — the receiver under your feet samples cascade 0, but cascade 0's depth
   * doesn't include the mountain peak, so it returns "lit" and the mountain
   * shadow vanishes. EVSM no longer cares about texel-size-driven normal
   * offset (smooth Chebyshev bound replaces the binary compare), so the
   * historical 12m cap is no longer load-bearing for acne suppression.
   */
  public maxCascade0Split = 25;

  constructor(shadowMapSize: number = 2048) {
    this.shadowMapSize = shadowMapSize;
  }

  destroy(): void {
    for (const tex of this.cascadeTextures) {
      tex.destroy();
    }
    this.cascadeTextures = [];
    for (const tex of this.staticCascadeTextures) {
      tex.destroy();
    }
    this.staticCascadeTextures = [];
    this.lastStaticCenter = [];
    this.lastStaticLightDirection = [];

    if (this._ctx) {
      const gl = this._ctx.gl;
      for (const fbo of this.momentsFBOs) gl.deleteFramebuffer(fbo);
      for (const tex of this.momentsTextures) {
        if (tex.glTexture) gl.deleteTexture(tex.glTexture);
      }
      if (this.momentsScratch1FBO) gl.deleteFramebuffer(this.momentsScratch1FBO);
      if (this.momentsScratch1?.glTexture) gl.deleteTexture(this.momentsScratch1.glTexture);
      if (this.momentsScratch2FBO) gl.deleteFramebuffer(this.momentsScratch2FBO);
      if (this.momentsScratch2?.glTexture) gl.deleteTexture(this.momentsScratch2.glTexture);
      if (this.fullscreenQuad) this.fullscreenQuad.destroy(gl);
    }
    this.momentsTextures = [];
    this.momentsFBOs = [];
    this.prevMomentsLightVP = [];
  }

  private _ctx?: Context;

  init(ctx: Context): void {
    this._ctx = ctx;
    this.depthRenderer = new DepthMapRenderer(DepthMode.CameraDistance);
    this.depthRenderer.init(ctx);
    this.fullscreenQuad = new FullscreenQuad(ctx.gl);

    for (let i = 0; i < CASCADE_COUNT; i++) {
      let tex = new RenderedTexture(true, {
        width: this.shadowMapSize,
        height: this.shadowMapSize,
      });
      tex.init(ctx);
      this.cascadeTextures.push(tex);
      this.cascadeViewMatrices.push(mat4.create());
      this.cascadeSplits.push(0);
      this.cascadeTexelSizes.push(0);

      const staticTex = new RenderedTexture(true, {
        width: this.shadowMapSize,
        height: this.shadowMapSize,
      });
      staticTex.init(ctx);
      this.staticCascadeTextures.push(staticTex);
      this.lastStaticCenter.push(null);
      this.lastStaticLightDirection.push(null);
      this.cachedLightView.push(null);
      this.cachedLightProjection.push(null);

      // RG16F moments target — HALF_FLOAT linear filtering is core WebGL2;
      // EXT_color_buffer_float (already required at DOMContextBuilder) covers
      // RG16F as a color attachment.
      const momentsTex = this.createMomentsTexture(ctx.gl);
      const momentsFBO = createFramebuffer(ctx.gl, momentsTex.glTexture!);
      this.momentsTextures.push(momentsTex);
      this.momentsFBOs.push(momentsFBO);
      this.prevMomentsLightVP.push(null);
    }

    // Two shared scratches for the resolve→blurH→blurV→blend chain. We can't
    // overwrite momentsTextures[i] mid-pass because it holds the EMA history
    // sampled by the lighting pass and used as `prev` for the blend.
    this.momentsScratch1 = this.createMomentsTexture(ctx.gl);
    this.momentsScratch1FBO = createFramebuffer(ctx.gl, this.momentsScratch1.glTexture!);
    this.momentsScratch2 = this.createMomentsTexture(ctx.gl);
    this.momentsScratch2FBO = createFramebuffer(ctx.gl, this.momentsScratch2.glTexture!);
  }

  private createMomentsTexture(gl: WebGL2RenderingContext): RenderTexture {
    // RG32F (not RG16F): half-float precision at the EVSM warped values
    // (M1 ≈ 148, M2 ≈ 22000 at d=1.0) is below the per-blur-kernel variance
    // signal on smooth slopes, so `M2 - M1²` becomes pure quantization noise
    // and produces visible concentric isoline ripples on terrain. Full-float
    // moments cost 2× the memory (32 → 64 MB at 4×2048²) but eliminate the
    // artifact and let us keep C_EVSM = 5 for good silhouette separation.
    const t = new RenderTexture();
    t.glTexture = createColorTexture(
      gl,
      this.shadowMapSize,
      this.shadowMapSize,
      gl.RG32F,
      gl.RG,
      gl.FLOAT,
      gl.LINEAR,
    );
    return t;
  }

  /** Set the instance manager for instanced shadow rendering. */
  public set instanceManager(im: InstanceManager | undefined) {
    this.depthRenderer.instanceManager = im;
  }

  computeAndRender(
    ctx: Context,
    scene: Scene,
    camera: Camera,
    lightDirection: vec3 | number[],
  ): void {
    this.frameCounter++;
    this.lastStaticCacheRebuildMask = 0;
    this.lightDirection = vec3.normalize(vec3.create(), lightDirection as vec3);

    // Pass the main camera position so LOD distance checks use view distance,
    // not the light-space cascade camera position.
    this.depthRenderer.mainCameraPosition = camera.cameraPosition;
    this.depthRenderer.lodDistanceScale = this.lodDistanceScale;

    // Cascade split distances are constant (depend only on near/far/lambda).
    // Pre-allocated in init() — fill in-place.
    // far=2000m covers km-scale outdoor terrain (LOD3 ring boundary at ~2.5km
    // chebyshev). lambda biases toward log distribution so the close cascades
    // stay tight (~26m / ~63m) while cascade 3 absorbs the long tail to 2km.
    const near = 0.1;
    const far = 2000.0;
    const lambda = 0.95; // blend between linear and logarithmic
    for (let i = 1; i <= CASCADE_COUNT; i++) {
      const p = i / CASCADE_COUNT;
      const log = near * Math.pow(far / near, p);
      const uniform = near + (far - near) * p;
      this.cascadeSplits[i - 1] = lambda * log + (1 - lambda) * uniform;
    }
    // Clamp cascade 0 so its frustum-slice radius (and hence texel size + ortho
    // width) stays close to the pre-big-scene value. Without this the cascade-0
    // texels grow ~2.6× and the player's shadow visibly Peter-Pans at grazing
    // angles. The remaining cascades absorb the extra range.
    if (this.cascadeSplits[0] > this.maxCascade0Split) {
      this.cascadeSplits[0] = this.maxCascade0Split;
    }

    // For each cascade, compute the light-space ortho projection.
    // Cascades 1 and 2 alternate frames to render only 2 of 3 cascades per frame.
    // When a cascade is skipped its depth texture and view matrix stay frozen from
    // the previous render — they must always match each other for correct shadow UVs.
    let prevSplit = near;
    for (let i = 0; i < CASCADE_COUNT; i++) {
      const splitFar = this.cascadeSplits[i];
      const rate = this.cascadeUpdateRates[i];
      // First-frame override: render every cascade at least once before
      // honoring the staggered schedule. Without this, cascades 2/3 stay
      // zero-initialised for 2–3 frames and the long-shadow overlay
      // (csm_shadow.glsl always samples cascade 3) reads M1=M2=0 →
      // Chebyshev returns ≈0 → entire scene reads fully shadowed during
      // the first ~50ms after every scene load.
      const shouldRender = this.firstRender || this.frameCounter % rate === i % rate;

      // Get frustum corners for this cascade slice
      const corners = this.getFrustumSliceCorners(camera, prevSplit, splitFar);

      // Compute bounding sphere of the frustum slice.
      // Using a sphere instead of a tight AABB ensures the projection size stays
      // constant regardless of camera rotation, eliminating shadow swimming.
      const center = vec3.create();
      for (const corner of corners) {
        vec3.add(center, center, corner);
      }
      vec3.scale(center, center, 1.0 / corners.length);

      let radius = 0;
      for (const corner of corners) {
        radius = Math.max(radius, vec3.distance(center, corner));
      }
      // Round up to avoid sub-texel jitter
      radius = Math.ceil(radius);

      // Texel size is constant (fixed FOV + cascade geometry) but cheap to recompute.
      this.cascadeTexelSizes[i] = (radius * 2) / this.shadowMapSize;

      if (!shouldRender) {
        prevSplit = splitFar;
        continue;
      }

      // Snap the cascade center to texel-aligned positions in light space to
      // prevent shadow edge swimming/shimmer as the camera moves.
      const texelSize = this.cascadeTexelSizes[i];

      // Rotation-only matrix (no translation) gives the light's orientation.
      // Transforming the cascade center into this frame yields meaningful X/Y
      // coordinates on the shadow-map plane that we can snap to the texel grid.
      const lightRotation = this.computeLightRotationMatrix();
      const rotatedCenter = vec3.transformMat4(vec3.create(), center, lightRotation);
      rotatedCenter[0] = Math.floor(rotatedCenter[0] / texelSize) * texelSize;
      rotatedCenter[1] = Math.floor(rotatedCenter[1] / texelSize) * texelSize;

      // Transform snapped position back to world space.
      const invRotation = mat4.invert(mat4.create(), lightRotation)!;
      const snappedCenter = vec3.transformMat4(vec3.create(), rotatedCenter, invRotation);

      const threshold = this.staticCacheThresholdTexels[i];
      const lastCenter = this.lastStaticCenter[i];
      const lastDir = this.lastStaticLightDirection[i];
      // Static-caster depth was rendered from the light's POV; rotating the sun
      // makes it stale even when the cascade center hasn't moved. Bust the cache
      // when the angular delta exceeds the threshold.
      const lightDirStale =
        lastDir === null ||
        vec3.dot(lastDir, this.lightDirection) < Math.cos(this.staticCacheLightAngleThreshold);
      const staticStale = !isFinite(threshold)
        ? false
        : lastCenter === null ||
          vec3.distance(snappedCenter, lastCenter) > threshold * texelSize ||
          lightDirStale;

      // Lock the matrix to the cache's snap position. Updating the matrix every
      // frame while the cache lags behind makes the static shadow drift relative
      // to its caster — looks like jitter at slow walking speeds. Recompute the
      // matrix only when we're about to rebuild the cache (or for un-cached
      // cascades, which always rebuild).
      const needNewMatrix =
        !isFinite(threshold) || staticStale || this.cachedLightView[i] === null;

      let lightView: mat4;
      let lightProjection: mat4;
      if (needNewMatrix) {
        lightView = this.computeLightViewMatrixAt(snappedCenter);
        // Asymmetric Z range. lightDirection is the rays direction (sun→scene),
        // so the cascade view's look-axis points away from the sun. In view
        // space toward-sun is +Z, away-from-sun is -Z. mat4.ortho's visible
        // Z = [-far, -near], so to extend deep toward the sun we make `near`
        // very negative; to keep the away-from-sun side tight we keep `far`
        // small. Symmetric margins wasted depth precision on the empty
        // away-from-sun direction while clipping real casters on the
        // toward-sun side (a tall mountain between the sun and a forward
        // receiver, e.g. behind the player).
        const AWAY_MARGIN = 30;
        lightProjection = mat4.ortho(
          mat4.create(),
          -radius,
          radius,
          -radius,
          radius,
          -this.casterDistanceToSun[i],
          radius + AWAY_MARGIN,
        );
        this.cachedLightView[i] = lightView;
        this.cachedLightProjection[i] = lightProjection;
        mat4.mul(this.cascadeViewMatrices[i], lightProjection, lightView);
      } else {
        lightView = this.cachedLightView[i]!;
        lightProjection = this.cachedLightProjection[i]!;
      }

      const cascadeCamera = new CascadeCamera(lightView, lightProjection, this.shadowMapSize);

      // Cascade 0 keeps the full-detail base mesh for sharp near shadows.
      // Cascades 1 and 2 route every visible caster through each group's
      // dedicated shadow mesh (typically the decimated mesh registered by the
      // LOD registrar). Coarser silhouette but ~4-10× fewer verts per tree, and
      // the lower angular detail is invisible at the distances those cascades cover.
      const useShadowLod = i !== 0;

      if (staticStale) {
        // Re-render static geometry (instanced trees/terrain) into the persistent cache.
        this.depthRenderer.shadowCasterFilter = 'static';
        this.depthRenderer.useShadowLod = useShadowLod;
        this.staticCascadeTextures[i].renderTexture(this.depthRenderer, scene, cascadeCamera);
        this.lastStaticCenter[i] = vec3.clone(snappedCenter);
        this.lastStaticLightDirection[i] = vec3.clone(this.lightDirection);
        this.lastStaticCacheRebuildMask |= 1 << i;
      }

      this.depthRenderer.useShadowLod = useShadowLod;
      this.depthRenderer.maxDynamicShadowDistance = this.maxDynamicShadowDistance[i];

      if (isFinite(threshold)) {
        // Blit cached static depth into the working cascade FBO, then render
        // dynamic objects (player, animated entities) on top without clearing.
        this.blitDepth(ctx.gl, this.staticCascadeTextures[i].framebuffer, this.cascadeTextures[i].framebuffer);
        this.depthRenderer.shadowCasterFilter = 'dynamic';
      } else {
        this.depthRenderer.shadowCasterFilter = 'all';
      }
      this.cascadeTextures[i].renderTexture(this.depthRenderer, scene, cascadeCamera);
      this.depthRenderer.shadowCasterFilter = 'all';

      // Resolve EVSM moments from the freshly-rendered cascade depth, then run
      // a separable Gaussian blur in moments space (preserves Chebyshev's
      // bound). The result is sampled by `csm_shadow.glsl` as a smooth
      // upper bound on visibility — replaces the binary depth compare that
      // produces texel-quantization stripes on slow-varying terrain.
      this.resolveMoments(ctx, i);

      prevSplit = splitFar;
    }
    this.firstRender = false;
  }

  /**
   * Run the moments-resolve + horizontal-blur + vertical-blur + temporal-EMA
   * sequence for one cascade. Output lands in `momentsTextures[cascadeIdx]`,
   * ready to be sampled by the lighting pass. Skipped naturally when the
   * depth pass is throttled because the call site sits inside the same
   * `shouldRender` branch.
   *
   * Buffer flow (scratch1 / scratch2 / moments[i] are the three RG32F slots):
   *   1. depth        → scratch1                    (resolve)
   *   2. scratch1     → scratch2                    (blur H)
   *   3. scratch2     → scratch1                    (blur V; scratch1 = fresh)
   *   4a. !history    → moments[i] = scratch1       (first install / cache reset)
   *   4b. history     → moments[i] = mix(prev, fresh, alpha)  (EMA blend)
   *
   * The two scratches are needed so the EMA can read `momentsTextures[i]` as
   * `prev` while writing the blended result somewhere else; you can't sample
   * and render to the same texture in one draw.
   */
  private resolveMoments(ctx: Context, cascadeIdx: number): void {
    const gl = ctx.gl;
    const sm = ctx.engine.shaderManager;
    const size = this.shadowMapSize;
    const texel: [number, number] = [1.0 / size, 1.0 / size];

    // Save state we modify; restore at end so the depth-renderer's next
    // iteration finds the GL exactly as it left it.
    const depthTestWasEnabled = gl.isEnabled(gl.DEPTH_TEST);
    const cullWasEnabled = gl.isEnabled(gl.CULL_FACE);
    const polyOffsetWasEnabled = gl.isEnabled(gl.POLYGON_OFFSET_FILL);
    const blendWasEnabled = gl.isEnabled(gl.BLEND);

    if (depthTestWasEnabled) gl.disable(gl.DEPTH_TEST);
    if (cullWasEnabled) gl.disable(gl.CULL_FACE);
    if (polyOffsetWasEnabled) gl.disable(gl.POLYGON_OFFSET_FILL);
    if (blendWasEnabled) gl.disable(gl.BLEND);

    gl.viewport(0, 0, size, size);

    // === Pass 1: depth → scratch1 (resolve) ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.momentsScratch1FBO);
    const resolveShader = sm.getCsmMomentsResolveShader();
    ctx.setShader(resolveShader);
    resolveShader.uniformsContainer.setDepthMap(this.cascadeTextures[cascadeIdx]);
    resolveShader.validate();
    this.fullscreenQuad.draw(gl);

    // === Pass 2: scratch1 → scratch2 (horizontal blur) ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.momentsScratch2FBO);
    const blurShader = sm.getCsmMomentsBlurShader();
    ctx.setShader(blurShader);
    blurShader.uniformsContainer
      .setMomentsMap(this.momentsScratch1)
      .setDirection([1, 0])
      .setTexelSize(texel);
    blurShader.validate();
    this.fullscreenQuad.draw(gl);

    // === Pass 3: scratch2 → scratch1 (vertical blur; scratch1 = fresh blurred) ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.momentsScratch1FBO);
    ctx.setShader(blurShader);
    blurShader.uniformsContainer
      .setMomentsMap(this.momentsScratch2)
      .setDirection([0, 1])
      .setTexelSize(texel);
    blurShader.validate();
    this.fullscreenQuad.draw(gl);

    // === Pass 4: install fresh into momentsTextures[cascadeIdx] ===
    // First install (no valid prev matrix) goes verbatim. Subsequent updates
    // run the *reprojecting* blend shader: it reconstructs each current-texel's
    // world position from the live cascade's inverse VP + the unblurred depth
    // sample, projects through the previous cascade's VP to find the matching
    // prev texel, and EMA-blends the world-coincident moments. This is what
    // makes the temporal smoothing survive matrix changes (sun rotation,
    // snap shifts, cache rebuilds) — naive same-UV blending mixed moments
    // for two different world points and produced wobble.
    const alpha = this.momentsBlendAlpha[cascadeIdx];
    const prevLightVP = this.prevMomentsLightVP[cascadeIdx];
    const currLightVP = this.cascadeViewMatrices[cascadeIdx];
    const useEma = prevLightVP !== null && alpha < 1.0;
    if (useEma) {
      const invCurrLightVP = mat4.invert(mat4.create(), currLightVP)!;
      ctx.textureState.clear();
      gl.bindFramebuffer(gl.FRAMEBUFFER, this.momentsScratch2FBO);
      const blendShader = sm.getCsmMomentsBlendShader();
      ctx.setShader(blendShader);
      blendShader.uniformsContainer
        .setPrev(this.momentsTextures[cascadeIdx])
        .setFresh(this.momentsScratch1)
        .setCurrDepth(this.cascadeTextures[cascadeIdx])
        .setInvCurrLightVP(invCurrLightVP)
        .setPrevLightVP(prevLightVP)
        .setAlpha(alpha);
      blendShader.validate();
      this.fullscreenQuad.draw(gl);
      // Blit the blended result back into momentsTextures[i].
      this.blitMoments(gl, this.momentsScratch2FBO, this.momentsFBOs[cascadeIdx]);
    } else {
      // First install or alpha=1.0 → fresh is the result. Blit into place.
      this.blitMoments(gl, this.momentsScratch1FBO, this.momentsFBOs[cascadeIdx]);
    }
    // Record current matrix as prev for next iteration's reprojection.
    if (this.prevMomentsLightVP[cascadeIdx] === null) {
      this.prevMomentsLightVP[cascadeIdx] = mat4.clone(currLightVP);
    } else {
      mat4.copy(this.prevMomentsLightVP[cascadeIdx]!, currLightVP);
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    if (depthTestWasEnabled) gl.enable(gl.DEPTH_TEST);
    if (cullWasEnabled) gl.enable(gl.CULL_FACE);
    if (polyOffsetWasEnabled) gl.enable(gl.POLYGON_OFFSET_FILL);
    if (blendWasEnabled) gl.enable(gl.BLEND);
  }

  private blitMoments(gl: WebGL2RenderingContext, src: WebGLFramebuffer, dst: WebGLFramebuffer): void {
    const s = this.shadowMapSize;
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, src);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, dst);
    gl.blitFramebuffer(0, 0, s, s, 0, 0, s, s, gl.COLOR_BUFFER_BIT, gl.NEAREST);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
  }

  private getFrustumSliceCorners(camera: Camera, near: number, far: number): vec3[] {
    // Use the *unjittered* projection. The TAA sub-pixel jitter (Halton 2,3
    // applied to projectionMatrix every frame) shifts FOV/aspect by a
    // fraction of a texel each frame; that propagates through to a tiny
    // bounding-sphere radius/center wobble, which in turn bumps the
    // light-space texel grid the cascade snaps to. Different snap → blur
    // kernel reads slightly different neighbour texels → moments fluctuate
    // → user sees "shades of shadow moving around at randomized offsets"
    // even with cascadeUpdateRates=[1,1,1,1] and EMA active. The shadow
    // pipeline must derive its frustum from the stable projection.
    const proj = camera.unjitteredProjection;

    // Extract FOV and aspect from the camera's projection matrix
    // For a perspective matrix: proj[5] = 1/tan(fov/2), proj[0] = proj[5]/aspect
    const tanHalfFov = 1.0 / proj[5];
    const aspect = proj[5] / proj[0];

    const nearHeight = near * tanHalfFov;
    const nearWidth = nearHeight * aspect;
    const farHeight = far * tanHalfFov;
    const farWidth = farHeight * aspect;

    // Camera position and orientation
    const camPos = camera.cameraPosition;
    const invView = mat4.invert(mat4.create(), camera.modelViewMatrix)!;

    // Extract camera axes from inverse view matrix
    const right = vec3.fromValues(invView[0], invView[1], invView[2]);
    const up = vec3.fromValues(invView[4], invView[5], invView[6]);
    const forward = vec3.fromValues(-invView[8], -invView[9], -invView[10]); // negative Z

    // Near plane center and far plane center
    const nearCenter = vec3.scaleAndAdd(vec3.create(), camPos, forward, near);
    const farCenter = vec3.scaleAndAdd(vec3.create(), camPos, forward, far);

    // 8 corners of the frustum slice
    const corners: vec3[] = [];

    // Near plane corners
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ]) {
      const corner = vec3.clone(nearCenter);
      vec3.scaleAndAdd(corner, corner, right, sx * nearWidth);
      vec3.scaleAndAdd(corner, corner, up, sy * nearHeight);
      corners.push(corner);
    }

    // Far plane corners
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ]) {
      const corner = vec3.clone(farCenter);
      vec3.scaleAndAdd(corner, corner, right, sx * farWidth);
      vec3.scaleAndAdd(corner, corner, up, sy * farHeight);
      corners.push(corner);
    }

    return corners;
  }

  /** Light orientation without translation — used for texel-grid snapping. */
  private computeLightRotationMatrix(): mat4 {
    const lightDir = this.lightDirection;
    let up = vec3.fromValues(0, 1, 0);
    if (Math.abs(vec3.dot(lightDir, up)) > 0.95) {
      up = vec3.fromValues(0, 0, 1);
    }
    const origin = vec3.fromValues(0, 0, 0);
    return mat4.lookAt(mat4.create(), origin, lightDir, up);
  }

  private computeLightViewMatrixAt(center: vec3): mat4 {
    const lightDir = this.lightDirection;

    // Choose an up vector that isn't parallel to the light direction
    let up = vec3.fromValues(0, 1, 0);
    if (Math.abs(vec3.dot(lightDir, up)) > 0.95) {
      up = vec3.fromValues(0, 0, 1);
    }

    // Position the camera at the cascade center, looking along the light direction
    const target = vec3.scaleAndAdd(vec3.create(), center, lightDir, 1);
    return mat4.lookAt(mat4.create(), center, target, up);
  }

  /** Reset the static cache for all cascades — call when static scene content changes. */
  public invalidateStaticCache(): void {
    for (let i = 0; i < CASCADE_COUNT; i++) {
      this.lastStaticCenter[i] = null;
      this.cachedLightView[i] = null;
      this.cachedLightProjection[i] = null;
      this.prevMomentsLightVP[i] = null;
    }
    // Force every cascade to render on the next frame so the lighting pass
    // doesn't read stale zero-initialised moments after a scene swap.
    this.firstRender = true;
  }

  private blitDepth(gl: WebGL2RenderingContext, src: WebGLFramebuffer, dst: WebGLFramebuffer): void {
    const s = this.shadowMapSize;
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, src);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, dst);
    gl.blitFramebuffer(0, 0, s, s, 0, 0, s, s, gl.DEPTH_BUFFER_BIT, gl.NEAREST);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
  }
}

// Minimal camera implementation for cascade shadow rendering
class CascadeCamera extends Camera {
  private _modelViewMatrix: mat4;
  private _projectionMatrix: mat4;
  private _frustum: ViewFrustum;

  constructor(modelView: mat4, projection: mat4, size: number) {
    super({ width: size, height: size });
    this.name = 'CascadeCamera';
    this._modelViewMatrix = modelView;
    this._projectionMatrix = projection;

    const vp = mat4.mul(mat4.create(), projection, modelView);
    const invVp = mat4.invert(mat4.create(), vp)!;
    this._frustum = new ViewFrustum(invVp);
  }

  get modelViewMatrix(): mat4 {
    return this._modelViewMatrix;
  }
  get projectionMatrix(): mat4 {
    return this._projectionMatrix;
  }
  get cameraPosition(): vec3 {
    return vec3.fromValues(0, 0, 0);
  }
  get targetPosition(): vec3 {
    return vec3.fromValues(0, 0, 0);
  }
  get frustum(): ViewFrustum {
    return this._frustum;
  }

  prepareFrame(): void {}
}
